import {
  Alert,
  Tabs,
  Text,
  Tooltip,
  getTheme,
  Button,
} from '@conexasaude/conexa-hero'
import { ChooseMultiprofileProps } from './types'
import { FunctionComponent, useCallback } from 'react'
import { Illustration } from '@/components/HideIllustrations'
import { RenderIf } from '@/components/RenderIf'
import { MainLayout } from '@/modules/Activation/components/MainLayout'
import UserCard from '../UserCard'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { useStyles } from './styles'
import { BackStepper } from '@/modules/Activation/components/BackStepper'
import Icon from '@/components/Iconography'
import { AnalyticEvent, Holder } from '@/modules/Signup/types/en-us'
import { SEND_TO } from '@/modules/Signup/constants'
import { usePatientRegistrationStore } from '@/modules/Signup/store/PatientRegistration.store'
import { useNavigation } from '@/hooks/useNavigation'
import { usePatientAccountStore } from '../../store/PatientAccount.store'

const ChoiceMultiprofile: FunctionComponent<ChooseMultiprofileProps> = ({
  activeTab,
  activeUsers,
  alertIsOpen,
  currentProfileId,
  inactiveUsers,
  loading,
  users,
  actionBackStepper,
  changeActiveTab,
  handleInactiveUserSelect,
  handleShowDependentsModal,
  handleUserSelect,
  updateSetActiveTab,
  enableMultiprofile,
  updateSetAlertIsOpen,
}) => {
  const styles = useStyles()
  const { logAnalyticsEvent } = useFirebaseContext()
  const theme = getTheme()
  const {
    setLoggedFlow,
    setHolder,
    setIsPrivateEligible,
    userData,
    setUserData,
  } = usePatientRegistrationStore()
  const { account } = usePatientAccountStore()
  const navigate = useNavigation()

  const settingUpAnalyticsEvent = useCallback(
    (eventDefinitions: AnalyticEvent) => {
      logAnalyticsEvent('at_patient_multiprofile', {
        interaction: eventDefinitions?.interaction,
        options: {
          sendTo: SEND_TO,
        },
      })
    },
    [logAnalyticsEvent],
  )

  const addNewAccount = () => {
    setLoggedFlow(true)
    fillHolderData()
    validatePrivateRegistration()
    navigate(account?.idAccount ? '/cadastro/conta' : '/cadastro/tipo-de-conta')
  }

  const fillHolderData = () => {
    const firstAccountHolder = activeUsers?.find(
      user => user?.holder === 'TITULAR',
    )
    const {
      token,
      name,
      birthdate,
      cellphone,
      email,
      clinicIdentifier,
      cpf,
      clinic,
    } = firstAccountHolder

    const holderData: Holder = {
      token: token,
      name: name,
      birthdate: birthdate,
      cellphone: cellphone,
      email: email,
      identifierType: clinicIdentifier,
      cpf: cpf,
      idClinic: clinic?.id,
      identifier: clinicIdentifier === 'CPF' ? firstAccountHolder?.cpf : null,
    }
    setUserData({
      ...userData,
      cpf: cpf,
      name: name,
      email: email,
      birthdate: birthdate,
      cellphone: cellphone,
    })
    setHolder(holderData)
  }

  const validatePrivateRegistration = () => {
    const hasPrivateAccount = !!users?.find(user => user?.clinic?.id === 2)
    const elegible = !hasPrivateAccount
    setIsPrivateEligible(elegible)
  }

  return (
    <MainLayout bodySize="430px" tagEventName="at_patient_multiprofile">
      <div css={styles.pageContainer}>
        <RenderIf condition={!account?.idAccount}>
          <div css={styles.backStepperWrapper}>
            <BackStepper
              onActionClick={actionBackStepper}
              color={theme.colors.neutral[200]}
              showTextButton
            />
          </div>
        </RenderIf>
        <RenderIf condition={inactiveUsers?.length > 0 && !!alertIsOpen}>
          <Alert
            action={changeActiveTab}
            actionLabel="Ver contas"
            title="Você possui contas pendentes de ativação."
            withCloseButton
            onClose={() => updateSetAlertIsOpen(false)}
            variant="information"
          />
        </RenderIf>
        <div css={styles.title}>
          <Text renderAs="h3" variant="headingMedium">
            Qual conta você quer utilizar?
          </Text>
          <Tooltip label="Dependentes adultos precisam criar uma conta separada.">
            <span>
              <Icon name="info" size="24px" color={theme.colors.neutral[200]} />
            </span>
          </Tooltip>
        </div>
        <RenderIf
          condition={users?.length > 1 || (users?.length && enableMultiprofile)}
        >
          <div css={styles.tabsContainer}>
            <Tabs
              fullWidth
              value={activeTab}
              onChange={updateSetActiveTab}
              defaultValue="activeUsers"
            >
              <Tabs.List>
                <Tabs.Tab
                  onClick={() => {
                    settingUpAnalyticsEvent({
                      interaction: 'active_accounts_click',
                    })
                  }}
                  value="activeUsers"
                >
                  Contas ativas
                </Tabs.Tab>
                <Tabs.Tab
                  onClick={() => {
                    settingUpAnalyticsEvent({
                      interaction: 'pending_accounts_click',
                    })
                  }}
                  notification={
                    inactiveUsers?.filter(
                      user =>
                        (user?.holder === 'DEPENDENTE' &&
                          !user?.isMinorPatient) ||
                        user?.holder === 'TITULAR',
                    )?.length || null
                  }
                  value="pendentUsers"
                >
                  Contas pendentes
                </Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="activeUsers">
                <div css={styles.cardsContainer}>
                  {activeUsers
                    ?.filter(user => user?.exibeLogin)
                    ?.map(user => {
                      return (
                        <UserCard
                          key={user?.id ? user?.id : user?.redirectUrl}
                          clinicUser={user}
                          onActionClick={() => handleUserSelect(user)}
                          isLoading={loading && currentProfileId === user?.id}
                          onLinkClick={() => handleShowDependentsModal(user)}
                        />
                      )
                    })}
                </div>
                <div css={styles.addNewAccountWrapper}>
                  <Button leftIcon="plus" onClick={addNewAccount}>
                    Adicionar nova conta
                  </Button>
                </div>
              </Tabs.Panel>
              <Tabs.Panel value="pendentUsers">
                <div css={styles.cardsContainer}>
                  {inactiveUsers?.map(user => {
                    if (user?.holder === 'DEPENDENTE' && user?.isMinorPatient) {
                      return null
                    }

                    return (
                      <UserCard
                        key={user?.id}
                        clinicUser={user}
                        onActionClick={() => handleInactiveUserSelect(user)}
                        isLoading={loading && currentProfileId === user?.id}
                      />
                    )
                  })}
                  <RenderIf
                    condition={
                      inactiveUsers?.filter(
                        user =>
                          user?.holder !== 'DEPENDENTE' ||
                          (user.holder === 'DEPENDENTE' &&
                            !user?.isMinorPatient),
                      )?.length === 0
                    }
                  >
                    <div css={styles.containerAllActiveAccounts}>
                      <div css={styles.containerIlustration}>
                        <Illustration
                          name="man-doing-thumbs-with-his-hand"
                          size="135.17px"
                        />
                      </div>
                      <Text variant="headingMedium">
                        Todas as suas contas estão ativas!
                      </Text>
                    </div>
                  </RenderIf>
                </div>
              </Tabs.Panel>
            </Tabs>
          </div>
        </RenderIf>
      </div>
    </MainLayout>
  )
}

export { ChoiceMultiprofile }
